import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { AppBar } from '@/components/AppBar';
import { PageContainer, PageInner } from '@/components/ui/PageContainer';
import { PageError, PageSkeleton } from '@/components/Skeleton';
import { QuizReview } from '@/components/QuizReview';
import { useAuth } from '@/lib/AuthProvider';
import { getAttempt, type AttemptDoc } from '@/lib/attempts';
import { getSectionDetailBySlug } from '@/lib/queries';
import { formatExamLabel, formatRelativeDate, formatDuration } from '@/lib/history';
import type { SectionDetail } from '@/lib/types';
import { AppError } from '@/lib/errors';

export const Route = createFileRoute('/history/attempts/$attemptId')({
  component: AttemptDetailScreen,
});

interface LoadedData {
  attempt: AttemptDoc;
  section: SectionDetail;
}

function AttemptDetailScreen() {
  const { attemptId } = Route.useParams();
  const navigate = useNavigate();
  const { user, isReady } = useAuth();
  const uid = user?.uid ?? null;

  const [data, setData] = useState<LoadedData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  // Redirect guests
  useEffect(() => {
    if (isReady && !uid) {
      navigate({ to: '/login' });
    }
  }, [isReady, uid, navigate]);

  useEffect(() => {
    if (!uid || !attemptId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const attempt = await getAttempt(uid, attemptId);
        if (!attempt) {
          throw new AppError('Lần làm bài này không tồn tại hoặc đã bị xoá.', {
            title: 'Không tìm thấy',
          });
        }
        const section = await getSectionDetailBySlug(attempt.courseSlug, attempt.sectionSlug);
        if (!section) {
          throw new AppError('Đề này không còn trong hệ thống.', {
            title: 'Không tìm thấy đề',
          });
        }
        if (cancelled) return;
        setData({ attempt, section });
      } catch (e) {
        if (cancelled) return;
        setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [uid, attemptId]);

  if (!isReady || (isReady && !uid)) return <PageSkeleton />;

  if (loading) {
    return (
      <PageContainer>
        <AppBar title="Chi tiết bài làm" onBack={() => navigate({ to: '/history' })} />
        <PageInner>
          <PageSkeleton />
        </PageInner>
      </PageContainer>
    );
  }

  if (error || !data) {
    return (
      <PageContainer>
        <AppBar title="Chi tiết bài làm" onBack={() => navigate({ to: '/history' })} />
        <PageInner>
          <PageError error={error ?? new AppError('Không tải được dữ liệu.')} />
        </PageInner>
      </PageContainer>
    );
  }

  const { attempt, section } = data;
  const examLabel = formatExamLabel(attempt.sectionSlug);
  const courseLabel = `${section.course.level} · ${section.course.mondai_name_vi}`;
  const pct = attempt.totalQuestions > 0 ? Math.round((attempt.correctCount / attempt.totalQuestions) * 100) : 0;

  return (
    <PageContainer>
      <AppBar
        title={`Đề ${examLabel}`}
        subtitle={courseLabel}
        onBack={() => navigate({ to: '/history' })}
      />
      <PageInner className="pt-4">
        <div className="mb-3 flex flex-wrap items-center gap-2 text-[12px] text-ink-2">
          <span className="rounded-full bg-surface-2 px-2.5 py-1 font-semibold">
            {formatRelativeDate(attempt.submittedAtMs)}
          </span>
          {attempt.durationSeconds > 0 && (
            <span className="rounded-full bg-surface-2 px-2.5 py-1 font-semibold tabular-nums">
              ⏱ {formatDuration(attempt.durationSeconds)}
            </span>
          )}
          <span
            className="rounded-full px-2.5 py-1 font-bold tabular-nums"
            style={{ background: 'var(--correct-bg)', color: 'var(--correct)' }}
          >
            {attempt.correctCount}/{attempt.totalQuestions} · {pct}%
          </span>
        </div>

        <QuizReview
          questions={section.questions}
          picks={attempt.picks}
          correctCount={attempt.correctCount}
          answeredCount={attempt.answeredCount}
          optCount={section.course.options_per_question || 3}
        />

        <div className="mt-6 flex gap-2">
          <Link
            to="/courses/$courseSlug/sections/$sectionSlug"
            params={{ courseSlug: attempt.courseSlug, sectionSlug: attempt.sectionSlug }}
            className="flex-1 rounded-xl bg-[var(--brand)] py-3 text-center text-[13px] font-bold text-white no-underline"
          >
            Làm lại đề này
          </Link>
          <Link
            to="/history"
            className="rounded-xl border border-border bg-surface px-4 py-3 text-[13px] font-medium text-ink-2 no-underline"
          >
            ← Lịch sử
          </Link>
        </div>
      </PageInner>
    </PageContainer>
  );
}
